import type React from 'react'
import { useCallback, useEffect, useMemo, useRef } from 'react'
import { FieldCoordinateSystem } from '@/utils'
import type { Coordinate, Drawing } from '@/types'
import { findAllSnapTargets, findClosestSegmentPosition } from '@/utils/Drawing.utils'
import { extractControlPoints, filterDrawingsNearCursor } from '@/utils/ControlPoint.utils'
import { PLAYER_RADIUS_FEET } from '@/constants'
import { NodeDeletePopup } from './NodeDeletePopup'
import { NodeAddPopup } from './NodeAddPopup'
import { useTheme } from '../../contexts/SettingsContext'
import { useControlPointDrag, usePopupState } from './hooks'
import { DrawingSnapIndicator } from './indicators/DrawingSnapIndicator'
import { PlayerSnapIndicator } from './indicators/PlayerSnapIndicator'

// Constants for proximity filtering
const DEFAULT_PROXIMITY_THRESHOLD = 20
const CONTROL_POINT_RADIUS = 6

type MultiDrawingControlPointOverlayProps = {
	drawings: Drawing[]
	selectedDrawingIds?: string[]
	players?: Array<{
		id: string
		x: number
		y: number
		label: string
		color: string
	}>
	coordSystem: FieldCoordinateSystem
	snapThreshold: number
	cursorPosition?: { x: number; y: number } | null
	proximityThreshold?: number
	zoom?: number
	panX?: number
	panY?: number
	onDragStart?: (
		_drawingId: string,
		_pointId: string,
		_x: number,
		_y: number,
	) => void
	onDragMove?: (
		_drawingId: string,
		_pointId: string,
		_x: number,
		_y: number,
	) => void
	onDragEnd?: (
		_drawingId: string,
		_pointId: string,
		_x: number,
		_y: number,
	) => void
	onMerge?: (
		_sourceDrawingId: string,
		_sourcePointId: string,
		_targetDrawingId: string,
		_targetPointId: string,
	) => void
	onLinkToPlayer?: (
		_drawingId: string,
		_pointId: string,
		_playerId: string,
	) => void
	onDeletePoint?: (_drawingId: string, _pointId: string) => void
	onAddPoint?: (_drawingId: string, _segmentIndex: number, _position: Coordinate) => void
	onPathContextMenuHandlerRef?: React.MutableRefObject<((
		_drawingId: string,
		_segmentIndex: number,
		_insertPosition: Coordinate,
		_pixelPosition: { x: number; y: number },
	) => void) | null>
	onAddPlayerAtNode?: (
		_drawingId: string,
		_pointId: string,
		_x: number,
		_y: number,
	) => void
	activeTool?: 'select' | 'addPlayer' | 'erase'
}

interface MultiDragState {
	drawingId: string // Which drawing owns the dragged point
	pointId: string // Which point is being dragged
	offsetX: number // Feet offset from cursor to node center (prevents jump on drag start)
	offsetY: number // Feet offset from cursor to node center
}

/**
 * Unified overlay that renders draggable control points for ALL drawings.
 * Eliminates race conditions from multiple overlay components.
 */
export function MultiDrawingControlPointOverlay({
													drawings,
													selectedDrawingIds = [],
													players,
													coordSystem,
													snapThreshold,
													cursorPosition,
													proximityThreshold = DEFAULT_PROXIMITY_THRESHOLD,
													zoom = 1,
													panX = 0,
													panY = 0,
													onDragStart,
													onDragMove,
													onDragEnd,
													onMerge,
													onLinkToPlayer,
													onDeletePoint,
													onAddPoint,
													onPathContextMenuHandlerRef,
													onAddPlayerAtNode,
													activeTool,
												}: MultiDrawingControlPointOverlayProps) {
	const { theme: _theme } = useTheme()
	const { contextMenuState, addPopupState, setContextMenuState, setAddPopupState } = usePopupState()
	const overlayRef = useRef<SVGGElement | null>(null)
	const lastDragPositionRef = useRef<{ x: number; y: number } | null>(null)

	// Drag state management with refs and window event listener
	const {
		dragState,
		snapTarget,
		playerSnapTarget,
		setDragState,
		setSnapTarget,
		setPlayerSnapTarget,
		cleanup,
	} = useControlPointDrag<MultiDragState>({
		onPointerUp: ({
						  dragState: currentDragState,
						  snapTarget: currentSnapTarget,
						  playerSnapTarget: currentPlayerSnapTarget,
					  }) => {
			const lastDragPosition = lastDragPositionRef.current

			if (!currentDragState) {
				return
			}

			// Get final position - use lastDragPosition if available (more recent than drawings state)
			// Otherwise fall back to reading from drawings (for initial drag start)
			let finalPosition: { x: number; y: number } | undefined
			if (lastDragPosition) {
				finalPosition = lastDragPosition
			} else {
				const drawing = drawings.find(d => d.id === currentDragState.drawingId)
				const point = drawing?.points[currentDragState.pointId]
				if (point) {
					finalPosition = { x: point.x, y: point.y }
				}
			}

			let didMergeOrLink = false

			if (currentPlayerSnapTarget && onLinkToPlayer) {
				onLinkToPlayer(
					currentDragState.drawingId,
					currentDragState.pointId,
					currentPlayerSnapTarget.playerId,
				)
				didMergeOrLink = true
			} else if (currentSnapTarget && onMerge) {
				onMerge(
					currentDragState.drawingId,
					currentDragState.pointId,
					currentSnapTarget.drawingId,
					currentSnapTarget.pointId,
				)
				didMergeOrLink = true
			}

			// Call onDragEnd with final position ONLY if we didn't merge or link
			// When merging/linking, onChange is already called with the correct state
			// Calling onDragEnd would use stale closures and overwrite the merge
			if (onDragEnd && finalPosition && !didMergeOrLink) {
				onDragEnd(currentDragState.drawingId, currentDragState.pointId, finalPosition.x, finalPosition.y)
			}

			// Clean up
			cleanup()
			lastDragPositionRef.current = null
		},
	})

	// SINGLE window pointermove handler
	const handlePointerMove = useCallback(
		function handlePointerMove(event: PointerEvent) {
			if (!dragState) return
			const svg = overlayRef.current?.ownerSVGElement
			if (!svg) return

			const rect = svg.getBoundingClientRect()
			const screenX = event.clientX - rect.left
			const screenY = event.clientY - rect.top
			const feet = coordSystem.screenToFeet(screenX, screenY, zoom, panX, panY)

			// Apply stored offset so node stays under cursor (prevents jump on drag start)
			let targetX = feet.x + dragState.offsetX
			let targetY = feet.y + dragState.offsetY

			// For snap points, constrain to path
			const drawing = drawings.find(d => d.id === dragState.drawingId)
			const point = drawing?.points[dragState.pointId]
			if (point?.type === 'snap' && drawing) {
				const segmentResult = findClosestSegmentPosition(
					drawing,
					{ x: targetX, y: targetY },
					coordSystem,
				)
				if (segmentResult) {
					targetX = segmentResult.insertPosition.x
					targetY = segmentResult.insertPosition.y
				}
			}

			// Store the last drag position for use in handlePointerUp
			lastDragPositionRef.current = { x: targetX, y: targetY }

			// Only call callback if provided
			if (onDragMove) {
				onDragMove(dragState.drawingId, dragState.pointId, targetX, targetY)
			}

			// Use unified snap target detection
			const thresholdFeet = snapThreshold / coordSystem.scale
			const snapResult = findAllSnapTargets({
				position: feet,
				players,
				drawings,
				excludeDrawingId: dragState.drawingId, // Exclude source drawing
				playerRadiusFeet: PLAYER_RADIUS_FEET,
				drawingThresholdFeet: thresholdFeet,
			})

			// Update state with snap results (refs auto-synced by hook)
			setPlayerSnapTarget(snapResult.playerSnapTarget)
			setSnapTarget(snapResult.drawingSnapTarget)
		},
		[
			dragState,
			onDragMove,
			coordSystem,
			drawings,
			players,
			snapThreshold,
			zoom,
			panX,
			panY,
		],
	)

	useEffect(() => {
		if (!dragState) return
		window.addEventListener('pointermove', handlePointerMove)
		return () => window.removeEventListener('pointermove', handlePointerMove)
	}, [dragState, handlePointerMove])

	// Expose path context menu handler to parent via ref
	useEffect(() => {
		if (onPathContextMenuHandlerRef) {
			onPathContextMenuHandlerRef.current = handlePathContextMenu
		}
		return () => {
			if (onPathContextMenuHandlerRef) {
				onPathContextMenuHandlerRef.current = null
			}
		}
	}, [onPathContextMenuHandlerRef])

	// Optimized: Single stable handler using data-attributes
	const handlePointerDown = useCallback(
		(event: React.PointerEvent<SVGCircleElement>) => {
			event.stopPropagation()
			const svg = overlayRef.current?.ownerSVGElement
			if (!svg) return

			const target = event.currentTarget
			const drawingId = target.getAttribute('data-drawing-id')
			const pointId = target.getAttribute('data-point-id')
			if (!drawingId || !pointId) return

			const rect = svg.getBoundingClientRect()
			const screenX = event.clientX - rect.left
			const screenY = event.clientY - rect.top
			const cursorFeet = coordSystem.screenToFeet(screenX, screenY, zoom, panX, panY)

			// Get current node position
			const drawing = drawings.find(d => d.id === drawingId)
			const point = drawing?.points[pointId]
			if (!point) return

			// Calculate offset: node center - cursor position
			// This prevents the node from jumping to the cursor on drag start
			const offsetX = point.x - cursorFeet.x
			const offsetY = point.y - cursorFeet.y

			const newDragState = { drawingId, pointId, offsetX, offsetY }
			setDragState(newDragState)
			lastDragPositionRef.current = { x: point.x, y: point.y }

			// Call onDragStart with initial position
			if (onDragStart) {
				onDragStart(drawingId, pointId, point.x, point.y)
			}
		},
		[coordSystem, drawings, onDragStart, panX, panY, setDragState, zoom],
	)

	// Optimized: Single stable handler for context menu using data-attributes
	const handleContextMenu = useCallback(
		(event: React.MouseEvent<SVGCircleElement>) => {
			event.preventDefault()
			event.stopPropagation()

			const target = event.currentTarget
			const drawingId = target.getAttribute('data-drawing-id')
			const pointId = target.getAttribute('data-point-id')
			const pixelX = Number.parseFloat(target.getAttribute('data-pixel-x') || '0')
			const pixelY = Number.parseFloat(target.getAttribute('data-pixel-y') || '0')

			if (!drawingId || !pointId) return

			setContextMenuState({
				drawingId,
				pointId,
				pixelPosition: { x: pixelX, y: pixelY },
			})
		},
		[setContextMenuState],
	)

	function handleDeleteFromPopup() {
		if (contextMenuState && onDeletePoint) {
			onDeletePoint(contextMenuState.drawingId, contextMenuState.pointId)
		}
		setContextMenuState(null)
	}

	function handleClosePopup() {
		setContextMenuState(null)
	}

	function handlePathContextMenu(
		drawingId: string,
		segmentIndex: number,
		insertPosition: Coordinate,
		pixelPosition: { x: number; y: number },
	) {
		// Only show add popup if not clicking on a control point
		setContextMenuState(null) // Close delete popup if open
		setAddPopupState({
			drawingId,
			segmentIndex,
			insertPosition,
			pixelPosition,
		})
	}

	function handleAddFromPopup() {
		if (addPopupState && onAddPoint) {
			onAddPoint(
				addPopupState.drawingId,
				addPopupState.segmentIndex,
				addPopupState.insertPosition,
			)
		}
		setAddPopupState(null)
	}

	// Optimized: Single stable handler for node click using data-attributes
	const handleNodeClick = useCallback(
		(event: React.MouseEvent<SVGCircleElement>) => {
			const target = event.currentTarget
			const drawingId = target.getAttribute('data-drawing-id')
			const pointId = target.getAttribute('data-point-id')
			if (!drawingId || !pointId) return

			if (activeTool === 'erase' && onDeletePoint) {
				event.stopPropagation()
				event.nativeEvent.stopImmediatePropagation()
				onDeletePoint(drawingId, pointId)
				return
			}

			if (activeTool === 'addPlayer' && onAddPlayerAtNode) {
				const x = Number.parseFloat(target.getAttribute('data-x') || '0')
				const y = Number.parseFloat(target.getAttribute('data-y') || '0')

				// Block if drawing already has a player
				const drawing = drawings.find(d => d.id === drawingId)
				if (drawing?.playerId) return

				// Stop event from bubbling to canvas click handler
				event.stopPropagation()
				event.nativeEvent.stopImmediatePropagation()
				onAddPlayerAtNode(drawingId, pointId, x, y)
			}
		},
		[activeTool, drawings, onAddPlayerAtNode, onDeletePoint],
	)

	// Show nodes for drawings that are EITHER hovered OR selected
	const drawingsToShowNodes = useMemo(
		() => filterDrawingsNearCursor(
			drawings,
			selectedDrawingIds,
			cursorPosition,
			coordSystem,
			proximityThreshold,
		),
		[drawings, cursorPosition, coordSystem, proximityThreshold, selectedDrawingIds],
	)

	// Collect control points only from drawings to show nodes for (memoized to avoid recalc every render)
	// No deduplication needed - each drawing's point pool already has unique points!
	const controlPoints = useMemo(
		() => extractControlPoints(drawingsToShowNodes),
		[drawingsToShowNodes],
	)

	// Control node colors from CSS variables (cached to avoid expensive DOM reads every render)
	const { nodeFill, nodeStroke } = useMemo(() => {
		const computedStyle = getComputedStyle(document.documentElement)
		return {
			nodeFill: computedStyle.getPropertyValue('--control-point-fill').trim() || 'white',
			nodeStroke: computedStyle.getPropertyValue('--control-point-stroke').trim() || 'black',
		}
	}, [_theme]) // Re-read when theme changes

	return (
		<g ref={overlayRef} pointerEvents="visiblePainted">
			{/* Render control points from shared pools */}
			{controlPoints.map((cp) => {
				const pixel = coordSystem.feetToPixels(cp.x, cp.y)
				return (
					<circle
						key={`${cp.drawingId}-${cp.pointId}`}
						cx={pixel.x}
						cy={pixel.y}
						r={CONTROL_POINT_RADIUS}
						fill={nodeFill}
						stroke={nodeStroke}
						strokeWidth={2}
						pointerEvents="all"
						data-drawing-id={cp.drawingId}
						data-point-id={cp.pointId}
						data-x={cp.x}
						data-y={cp.y}
						data-pixel-x={pixel.x}
						data-pixel-y={pixel.y}
						style={{
							cursor: activeTool === 'erase'
								? 'pointer'
								: activeTool === 'addPlayer'
									? 'pointer'
									: dragState
										? 'grabbing'
										: 'grab',
						}}
						onPointerDown={activeTool === 'select' ? handlePointerDown : undefined}
						onClick={handleNodeClick}
						onContextMenu={activeTool === 'select' ? handleContextMenu : undefined}
					/>
				)
			})}

			{/* Snap indicator for drawing endpoints */}
			{snapTarget && (
				<DrawingSnapIndicator
					coordSystem={coordSystem}
					snapPoint={snapTarget.point}
				/>
			)}

			{/* Snap indicator for player positions */}
			{playerSnapTarget && (
				<PlayerSnapIndicator
					coordSystem={coordSystem}
					playerPosition={playerSnapTarget.point}
				/>
			)}

			{/* Delete node popup */}
			{contextMenuState && (
				<foreignObject
					x={0}
					y={0}
					width="100%"
					height="100%"
					pointerEvents="none"
					style={{ overflow: 'visible' }}
				>
					<div style={{ pointerEvents: 'auto' }}>
						<NodeDeletePopup
							position={contextMenuState.pixelPosition}
							onDelete={handleDeleteFromPopup}
							onClose={handleClosePopup}
						/>
					</div>
				</foreignObject>
			)}

			{/* Add node popup */}
			{addPopupState && (
				<foreignObject
					x={0}
					y={0}
					width="100%"
					height="100%"
					pointerEvents="none"
					style={{ overflow: 'visible' }}
				>
					<div style={{ pointerEvents: 'auto' }}>
						<NodeAddPopup
							position={addPopupState.pixelPosition}
							onAdd={handleAddFromPopup}
							onClose={() => setAddPopupState(null)}
						/>
					</div>
				</foreignObject>
			)}
		</g>
	)
}
